import { useState, type FormEvent } from 'react';
import { login, type LoginResult } from '@/api';
import type { AuthState } from '@/config';

interface Props {
  onLogin: (auth: AuthState) => void;
  onLoginError: (msg: string | null) => void;
}

export default function LoginForm({ onLogin, onLoginError }: Props) {
  const [user, setUser] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    onLoginError(null);
    try {
      const { ok, status, data } = await login(user, password);
      if (ok && (data as LoginResult).csrfToken) {
        onLogin({
          user: (data as LoginResult).user ?? user,
          role: ((data as LoginResult).role as AuthState['role']) ?? 'viewer',
          csrfToken: (data as LoginResult).csrfToken!,
        });
      } else {
        setError((data as LoginResult).error || `Login failed (HTTP ${status})`);
      }
    } catch {
      setError('Could not reach the backend.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-sm rounded-xl bg-white p-8 shadow">
        <h1 className="text-center text-2xl font-semibold text-slate-900">NallaSMP Control</h1>
        <p className="mt-1 text-center text-sm text-slate-500">Start and stop the Minecraft server.</p>
        {error && <p className="mt-4 text-sm text-rose-600">{error}</p>}
        <form className="mt-6 space-y-4" onSubmit={handleSubmit} autoComplete="off">
          <div>
            <label className="block text-sm font-medium text-slate-700">User</label>
            <input
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
              type="text" value={user} onChange={(e) => setUser(e.target.value)}
              placeholder="admin" autoComplete="off" maxLength={64} required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Password</label>
            <input
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
              type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••" autoComplete="off" maxLength={256} required
            />
          </div>
          <button type="submit" className="btn w-full bg-sky-600 text-white hover:bg-sky-700 disabled:bg-sky-400" disabled={submitting}>
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}
